<template>
  <v-row class="wizard-intro" no-gutters>
    <v-col cols="12" md="6" class="pa-6 d-flex flex-column justify-center">
      <h1 class="text-h3 font-weight-bold">{{ $t("rollOffWizard.intro.title") }}</h1>
      <div class="mt-8" style="max-width:500px;">
        <v-form ref="introForm" v-model="introFormValid" lazy-validation>
          <GoogleAddress
            :reCaptcha="reCaptcha"
            :newstoreapi="newstoreapi"
            :lang="lang"
            :country="country"
            v-model="userStore.form.address"
            v-bind:rules="formRules.addressRules"
            :no-label="$t('rollOffWizard.intro.addressLabel')"
          />
          <v-card-text class="text-left px-0 py-0 mb-1 text-label">
            {{ $t("rollOffWizard.intro.dateLabel") }}
            <span class="float-right grey--text lighten-1">{{$t("optional")}}</span>
          </v-card-text>
          <v-menu
            v-model="userStore.form.wantedDeliveryDateMenu"
            :close-on-content-click="false"
            offset-y
            max-width="290px"
            min-width="auto"
            top
            attach="attach"
            v-bind:disabled="loading"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                readonly
                flat
                v-model="userStore.form.wantedDeliveryDateFormatted"
                dense
                outlined
                :placeholder="$t('select')"
                :hint="canada ? 'YYYY-MM-DD' : 'MM/DD/YYYY'"
                persistent-hint="persistent-hint"
                v-bind:rules="canada ? formRules.dateCanadaRules : formRules.dateRules"
                solo
                class="rounded-lg input-label"
                v-bind="attrs"
                v-on="on"
                v-bind:disabled="loading"
                color="grey"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="userStore.form.wantedDeliveryDate"
              no-title="no-title"
              v-bind:min="form.minDate"
              v-bind:max="form.maxDate"
              v-bind:locale="lang?.toLowerCase()+'-'+ (!canada ? 'US' : 'CA')"
              v-on:input="form.wantedDeliveryDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-form>
      </div>
    </v-col>
    <v-col cols="12" md="6" class="d-flex flex-column justify-center">
      <v-stepper v-model="stepperModel" vertical flat class="mt-3">    
        <v-stepper-step class="py-0" complete color="secondary" complete-icon="mdi-numeric-1" step="1">
          <v-row class="d-flex align-center" no-gutters>
            <v-col>
              <div class="semi-bold" style="font-size:18px">{{ $t("rollOffWizard.intro.step1") }}</div>
            </v-col>
            <v-col cols="auto">
              <v-img src="https://embed.wasteconnections.com/images-widget/roll-off-wizard/Onboarding+Screen/onboarding_step1.png" style="width:100px;height:100px;margin-top:4px;" :alt="$t('rollOffWizard.intro.step1Alt')" />
            </v-col>
          </v-row>
        </v-stepper-step>
        <v-stepper-content step="1" />
        
        <v-stepper-step class="py-0" complete color="secondary" complete-icon="mdi-numeric-2" step="2">
          <v-row class="d-flex align-center" no-gutters>
            <v-col>
              <div class="semi-bold" style="font-size:18px">{{ $t("rollOffWizard.intro.step2") }}</div>
            </v-col>
            <v-col cols="auto">
              <v-img src="https://embed.wasteconnections.com/images-widget/roll-off-wizard/Onboarding+Screen/onboarding_step2.png" style="width:100px;height:100px;margin-top:4px;" :alt="$t('rollOffWizard.intro.step2Alt')" />
            </v-col>
          </v-row>
        </v-stepper-step>
        <v-stepper-content step="2" />

        <v-stepper-step class="py-0" complete color="secondary" complete-icon="mdi-numeric-3" step="3">
          <v-row class="d-flex align-center" no-gutters>
            <v-col>
              <div class="semi-bold" style="font-size:18px">{{ $t("rollOffWizard.intro.step3") }}</div>
            </v-col>
            <v-col cols="auto">
              <v-img src="https://embed.wasteconnections.com/images-widget/roll-off-wizard/Onboarding+Screen/onboarding_step3.png" style="width:100px;height:100px;margin-top:4px;" :alt="$t('rollOffWizard.intro.step3Alt')" />
            </v-col>
          </v-row>
        </v-stepper-step>
      </v-stepper>
    </v-col>
  </v-row>
</template>

<script setup>
import { computed, watch, ref, defineExpose } from 'vue'
import GoogleAddress from '../sfc/GoogleAddress.vue'
import { useIndexStore } from '@/store/index'
import { useUserStore } from '@/store/user'
import { useRollOffWizardStore } from '@/store/rollOffWizard'

// Stepper model variable, initialized to 0 to hide the default stepper step
const stepperModel = ref(0)

// Variables for form validation
const introForm = ref(null)
const introFormValid = ref(true)

// Importing stores
const indexStore = useIndexStore()
const userStore = useUserStore()
const rollOffWizardStore = useRollOffWizardStore()

// Computed from indexStore
const formRules = computed(() => indexStore.formRules)
const loading = computed(() => indexStore.loading)

// Computed from userStore
const form = computed({
  get: () => userStore.form,
  set: val => (userStore.form = val),
})

// Computed from rollOffWizardStore
const canada = computed(() => rollOffWizardStore.canada)
const reCaptcha = computed(() => rollOffWizardStore.reCaptcha)
const newstoreapi = computed(() => rollOffWizardStore.newstoreapi)
const lang = computed(() => rollOffWizardStore.lang)
const country = computed(() => rollOffWizardStore.country)

watch(
  () => userStore.form.wantedDeliveryDate,
  (newVal) => {
    if (newVal) {
      indexStore.formatDate(
        newVal,
        canada.value ? 'en_CA' : '',
        'wantedDeliveryDateFormatted'
      )
    } else {
      userStore.form.wantedDeliveryDateFormatted = ''
    }
  }
)

function validateIntroForm() {
  return introForm.value?.validate();
}

defineExpose({ validateIntroForm });
</script>

<style scoped>
::v-deep .v-stepper__step__step {
  height: 36px;
  width: 36px;
}

::v-deep .v-stepper__step__step .mdi-numeric-1::before,
::v-deep .v-stepper__step__step .mdi-numeric-2::before,
::v-deep .v-stepper__step__step .mdi-numeric-3::before {
  font-size:30px;
}
</style>
